import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  CompleteMailingListRequestRequestSchema,
  CompleteMailingListRequestResponseSchema,
  CreateMailingListRequestsRequestSchema,
  CreateMailingListRequestsResponseSchema,
  CreateMailingListReviewDraftRequestSchema,
  CreateMailingListReviewDraftResponseSchema,
  DiscoverMailingListsRequestSchema,
  DiscoverMailingListsResponseSchema,
  GetLatestMailingListReviewDraftResponseSchema,
  GetMailingListEmailsResponseSchema,
  ListMailingListOperationsResponseSchema,
  ListMailingListsQuerySchema,
  ListMailingListsResponseSchema,
  PrepareMailingListUrlRequestSchema,
  PrepareMailingListUrlResponseSchema,
  PrepareMailingListRequestResponseSchema,
  RevealMailingListUrlRequestSchema,
  RevealMailingListUrlResponseSchema,
  SetMailingListPreferenceRequestSchema,
  SetMailingListPreferenceResponseSchema,
} from '../schemas/mailing-lists';
import { defaultErrorResponses } from './errors';

const c = initContract();

export const mailingListsContract = c.router({
  discover: {
    method: 'POST',
    path: '/v1/mailing-lists/discover',
    body: DiscoverMailingListsRequestSchema,
    responses: { 200: DiscoverMailingListsResponseSchema, ...defaultErrorResponses },
    metadata: { auth: true, summary: 'Discover a bounded page of mailing lists' },
  },

  getMailingLists: {
    method: 'GET',
    path: '/v1/mailing-lists',
    query: ListMailingListsQuerySchema,
    responses: {
      200: ListMailingListsResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'List identified mailing lists with metrics and recommendation state',
    },
  },

  getMailingListEmails: {
    method: 'GET',
    path: '/v1/mailing-lists/:id/emails',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    query: z.object({
      cursor: z.string().optional(),
      limit: z.coerce.number().int().min(1).max(50).optional(),
    }),
    responses: {
      200: GetMailingListEmailsResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Get observed sender emails and evidence scope for a specific mailing list',
    },
  },

  prepareUrl: {
    method: 'POST',
    path: '/v1/mailing-lists/:id/url/prepare',
    pathParams: z.object({ id: z.uuid() }),
    body: PrepareMailingListUrlRequestSchema,
    responses: { 200: PrepareMailingListUrlResponseSchema, ...defaultErrorResponses },
    metadata: { auth: true, summary: 'Prepare an explicit mailing-list URL review' },
  },

  revealUrl: {
    method: 'POST',
    path: '/v1/mailing-lists/:id/url/reveal',
    pathParams: z.object({ id: z.uuid() }),
    body: RevealMailingListUrlRequestSchema,
    responses: { 200: RevealMailingListUrlResponseSchema, ...defaultErrorResponses },
    metadata: { auth: true, summary: 'Reveal a reviewed mailing-list URL' },
  },

  prepareRequest: {
    method: 'POST',
    path: '/v1/mailing-lists/:id/request/prepare',
    pathParams: z.object({ id: z.uuid() }),
    body: PrepareMailingListUrlRequestSchema,
    responses: { 200: PrepareMailingListRequestResponseSchema, ...defaultErrorResponses },
    metadata: { auth: true, summary: 'Check a mailing-list one-click request before review' },
  },

  setMailingListPreference: {
    method: 'POST',
    path: '/v1/mailing-lists/:id/preference',
    pathParams: z.object({
      id: z.string().uuid(),
    }),
    body: SetMailingListPreferenceRequestSchema,
    responses: {
      200: SetMailingListPreferenceResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Update user preference for a mailing list (keep_subscribed or dismissed)',
    },
  },

  createReviewDraft: {
    method: 'POST',
    path: '/v1/mailing-list-review-drafts',
    body: CreateMailingListReviewDraftRequestSchema,
    responses: {
      200: CreateMailingListReviewDraftResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Save an expiring review selection draft across Stripe checkout',
    },
  },

  getLatestReviewDraft: {
    method: 'GET',
    path: '/v1/mailing-list-review-drafts/latest',
    responses: {
      200: GetLatestMailingListReviewDraftResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Get the latest active review draft for restoring post-checkout state',
    },
  },

  createRequests: {
    method: 'POST',
    path: '/v1/mailing-list-requests',
    body: CreateMailingListRequestsRequestSchema,
    responses: {
      200: CreateMailingListRequestsResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Submit verified unsubscribe requests (Pro only, daily cap enforced)',
    },
  },

  listRequests: {
    method: 'GET',
    path: '/v1/mailing-list-requests',
    query: z.object({
      limit: z.coerce.number().int().min(1).max(100).optional(),
    }),
    responses: {
      200: ListMailingListOperationsResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'List recent unsubscribe operations and audit history',
    },
  },

  completeRequest: {
    method: 'POST',
    path: '/v1/mailing-list-requests/:id/complete',
    pathParams: z.object({ id: z.uuid() }),
    body: CompleteMailingListRequestRequestSchema,
    responses: {
      200: CompleteMailingListRequestResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Complete a browser-dispatched mailing list request',
    },
  },
});
